import React, { useState, useEffect } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { Layout, Tabs } from "antd";
import { isMobile, isTablet } from "react-device-detect";

import { CardsProvider } from "../components/contexts/cardcontext";
import { GraphProvider } from "../components/contexts/graphcontext";
import { useModalContext } from "../components/contexts/modalcontext";
import { SearchProvider } from "../components/contexts/searchcontext.ts";
import { PopularChartProvider, POPULAR_CHART_TYPES } from "../components/contexts/popularchartcontext";
import ChartPane from "../components/layouts/chartpane";
import AuthenticatedRoute from "../components/authenticatedroute";
import CardFilterWizard from "../components/menus/cardfilterwizardmenu";
import useWindowWidth from "../components/utilities";
import { SAVED_SEARCH_TYPES } from "../globals";
import UserTrackingWrapper from "../components/wrappers/UserTrackingWrapper";
import SaveToFavoritesModal from "../components/modals/SaveToFavoritesModal";
import MultipleEntitiesMenu from "../components/menus/MultipleEntitiesMenu";
import { FetchFavorites } from "../components/favorites/FetchFavorites";
import { ENTITY_TYPES } from "../utils/constants";
import PopCountChartModal from "../components/modals/PopCountChartModal";

const { Content, Sider } = Layout;
const { TabPane } = Tabs;
const { PLAYER_CHARTS } = SAVED_SEARCH_TYPES;

const siderWidth = 340;

function widthDeterminedText(wWidth, collapsed) {
  return wWidth > 1024 ? (collapsed ? "SHOW" : "HIDE") : collapsed ? "SEARCH" : "RESULTS";
}

function widthDeterminedArrow(wWidth, collapsed) {
  return wWidth > 1024 ? (
    collapsed ? (
      <CaretDownOutlined />
    ) : (
      <CaretUpOutlined />
    )
  ) : collapsed ? (
    <CaretDownOutlined />
  ) : (
    <CaretUpOutlined />
  );
}

const SidebarCollapsed = ({ cid, searchFields, range, from, to, isAdvancedSearch }) => {
  const wWidth = useWindowWidth();
  const [collapsed, setCollapsed] = useState(false);

  const showMobile = isMobile && !isTablet;
  const requiredCount = searchFields.length;

  const renderWizard = () => {
    return isAdvancedSearch ? (
      <CardFilterWizard
        collapseSider={(e) => setCollapsed(e)}
        requiredCount={requiredCount}
        cardId={cid}
        queryRange={range}
        from={from}
        to={to}
        activeFields={["Search"]}
        searchFields={searchFields}
        savedSearchType={PLAYER_CHARTS}
      />
    ) : (
      <MultipleEntitiesMenu
        entityIds={cid}
        entityType={ENTITY_TYPES.CARD}
        savedSearchType={PLAYER_CHARTS}
        collapseSider={(e) => setCollapsed(e)}
      />
    );
  };

  return (
    <>
      <div className={`search-filter-popup pcc-sidebar-mobile-visibility ${!collapsed ? "fixed-menu" : ""}`}>
        <button onClick={() => setCollapsed(!collapsed)} className="mobile-search-btn">
          Search Cards {collapsed ? <CaretUpOutlined /> : <CaretDownOutlined />}
        </button>
        <div className="filter-wizard-wrapper" style={{ display: collapsed ? "none" : "block" }}>
          {showMobile ? renderWizard() : null}
        </div>
      </div>
      <Sider
        breakpoint="lg"
        collapsible
        collapsed={collapsed}
        collapsedWidth={0}
        width={siderWidth}
        style={{ height: "100vh" }}
        className={"pcc-sidebar-desktop-visibility"}
        trigger={null}
      >
        <div
          className={`ant-layout-sider-zero-width-trigger ${collapsed ? "collapsed" : ""}`}
          onClick={() => setCollapsed(!collapsed)}
        >
          <div>{widthDeterminedArrow(wWidth, collapsed)}</div>
          <p className={"sider-search"}>{widthDeterminedText(wWidth, collapsed)}</p>
        </div>
        {!showMobile ? renderWizard() : null}
      </Sider>
    </>
  );
};

const PlayerCharts = () => {
  const router = useRouter();
  const { isPopCountChartModalVisible, setIsPopCountChartModalVisible, popCountChartCardId } = useModalContext();
  const { cid, range, from, to } = router.query;
  const searchFields = ["players", "setNameYears", "variations", "grades"];

  const [isReady, setReady] = useState(false);
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(true);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    setReady(router.isReady);
  }, [router.isReady]);

  useEffect(() => {
    if (!cid || (cid && cid.split(",").length === 1)) {
      setIsAdvancedSearch(true);
    } else {
      setIsAdvancedSearch(false);
    }
  }, [cid]);

  const content = (
    <Layout className="player-charts-page">
      <SidebarCollapsed
        {...{
          cid,
          range,
          from,
          to,
          searchFields,
          isAdvancedSearch,
        }}
      />
      <Content className={"app-content-padded content-height-restricted"}>
        <PopCountChartModal
          cardId={popCountChartCardId}
          visible={isPopCountChartModalVisible}
          onCancel={() => setIsPopCountChartModalVisible(false)}
        />
        <SaveToFavoritesModal />
        <FetchFavorites />
        <ChartPane
          hasTabs
          playerCharts={true}
          searchFields={searchFields}
          filterFields={[]}
          showSavedSearches={true}
          showMarketCapStat={true}
          savedSearchType={PLAYER_CHARTS}
          range={range}
        />
        <div style={{ marginTop: 60, marginLeft: 10 }}>
          <h2 style={{ marginLeft: "1rem" }}>Instructions &amp; Help</h2>
          <Tabs>
            <TabPane tab={"About"} key={0}>
              <p style={{ padding: "0 32px", textAlign: "left", fontSize: 16, margin: "15px 0 30px" }}>
                See the price history and stats of a player’s most popular cards. If the card you are looking for isn't
                here, request it by clicking Support in the lower right, and then use the{" "}
                <a href="/livesearch">Chart Other Cards</a> feature to find its price history right now.
              </p>
            </TabPane>
            <TabPane key={1} tab={"How to use the filters"}>
              <p style={{ padding: "0 32px", textAlign: "left", fontSize: 16, margin: "15px 0 30px" }}>
                To ensure the accuracy of our data, we only include the most popular players, sets, and variations.{" "}
                <br /> The most popular cards have the most transaction volume, so they give you the best understanding
                of a player's price movements. <br /> To request a card to be added, click Support in the bottom right.{" "}
                <br />
                <strong>Compare multiple players against each other!</strong> Just select multiple players before
                searching.
              </p>
            </TabPane>
            <TabPane key={2} tab={"Add a player or card"}>
              <p style={{ padding: "0 32px", textAlign: "left", fontSize: 16, margin: "15px 0 30px" }}>
                Is there a player or card you'd like us to add? Request that we add them by clicking the Support button
                in the lower right. We generally add cards within 7-14 days of your request.
              </p>
            </TabPane>
            <TabPane key={3} tab={"Support"}>
              <p style={{ padding: "0 32px", textAlign: "left", fontSize: 16, margin: "15px 0 30px" }}>
                For support, click the Support button in the lower right of your screen.
              </p>
            </TabPane>
          </Tabs>
        </div>
      </Content>
    </Layout>
  );

  return (
    <AuthenticatedRoute>
      <Head>
        <title>Popular Card Charts</title>
      </Head>
      <UserTrackingWrapper>
        <CardsProvider>
          <PopularChartProvider type={POPULAR_CHART_TYPES.CARD}>
            {isReady && (
              <SearchProvider range={range}>
                <GraphProvider>{content}</GraphProvider>
              </SearchProvider>
            )}
          </PopularChartProvider>
        </CardsProvider>
      </UserTrackingWrapper>
    </AuthenticatedRoute>
  );
};

export default PlayerCharts;
